/* ═══════════════════════════════════════════════════
   GAMEPLANS CARDS — Filter Logic & Editor Support
   Pattern adapted from collage-tabs.js (tabs-component)
   ═══════════════════════════════════════════════════ */

if (!customElements.get("gameplan-cards-component")) {
  class GameplanCards extends HTMLElement {
    constructor() {
      super();
      this.filterBtns = this.querySelectorAll("[data-gp-filters] .gp-filter-btn");
      this.grid = this.querySelector("[data-gp-grid]");
      this.cards = this.grid ? Array.from(this.grid.querySelectorAll(".gp-card")) : [];
      this.activeFilter = "all";
    }

    connectedCallback() {
      this.bindFilters();
      this.applyGridPattern(this.cards);
    }

    /* ── Filter button click handling ── */
    bindFilters() {
      this.filterBtns.forEach((btn) => {
        btn.addEventListener("click", () => {
          const filter = btn.dataset.filter;
          if (filter === this.activeFilter) return;

          this.activeFilter = filter;

          /* Update active state on buttons */
          this.filterBtns.forEach((b) => {
            b.classList.remove("active");
            b.setAttribute("aria-selected", "false");
          });
          btn.classList.add("active");
          btn.setAttribute("aria-selected", "true");

          this.filterCards(filter);
        });
      });
    }

    /* ── Show / hide cards based on card_types (multi-select) ── */
    filterCards(filter) {
      const visible = [];

      this.cards.forEach((card) => {
        const types = (card.dataset.cardTypes || "")
          .split(",")
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean);

        if (filter === "all" || types.includes(filter)) {
          card.classList.remove("gp-card--hidden");
          visible.push(card);
        } else {
          card.classList.add("gp-card--hidden");
        }
      });

      /* Re-apply 2→3→2 grid pattern to only the visible cards */
      this.applyGridPattern(visible);

      /* Replay entrance animation on newly visible cards */
      this.replayAnimation(visible);
    }

    /* ── Replay scroll-trigger animation on visible cards ── */
    replayAnimation(visibleCards) {
      /* 1. Hide cards instantly & strip animation classes */
      visibleCards.forEach((card) => {
        const animClass = [...card.classList].find((c) => c.startsWith("animate--"));
        if (!animClass) return;
        card._gpAnimClass = animClass;
        card.classList.add("gp-card--pre-anim");
        card.classList.remove("m-scroll-trigger", animClass);
      });

      /* 2. Wait two frames so the browser paints the hidden state,
            then re-add animation classes for a clean entrance */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          visibleCards.forEach((card) => {
            if (card._gpAnimClass) {
              card.classList.remove("gp-card--pre-anim");
              card.classList.add("m-scroll-trigger", card._gpAnimClass);
            }
          });
        });
      });
    }

    /* ── Assign half/third classes in 2-3-2 cycle ── */
    applyGridPattern(visibleCards) {
      visibleCards.forEach((card, i) => {
        const pos = i % 7;
        card.classList.remove("gp-card--half", "gp-card--third");

        if (pos < 2 || pos >= 5) {
          card.classList.add("gp-card--half");
        } else {
          card.classList.add("gp-card--third");
        }

        /* Update animation order for stagger */
        card.style.setProperty("--animation-order", i + 1);
      });
    }

    /* ── Reset to default state ── */
    reset() {
      this.filterBtns = this.querySelectorAll("[data-gp-filters] .gp-filter-btn");
      this.grid = this.querySelector("[data-gp-grid]");
      this.cards = this.grid ? Array.from(this.grid.querySelectorAll(".gp-card")) : [];
      this.activeFilter = "all";

      this.filterBtns.forEach((b) => {
        b.classList.remove("active");
        b.setAttribute("aria-selected", "false");
      });
      const allBtn = this.querySelector('[data-filter="all"]');
      if (allBtn) {
        allBtn.classList.add("active");
        allBtn.setAttribute("aria-selected", "true");
      }

      this.cards.forEach((c) => c.classList.remove("gp-card--hidden"));
      this.applyGridPattern(this.cards);
      this.bindFilters();
    }
  }

  customElements.define("gameplan-cards-component", GameplanCards);
}

/* ── Shopify editor: re-initialize on section re-render ── */
if (Shopify.designMode) {
  document.addEventListener("shopify:section:load", (e) => {
    const section = e.target;
    const component = section.querySelector("gameplan-cards-component");
    if (component) component.reset();
  });

  /* Scroll selected block into view in the editor */
  document.addEventListener("shopify:block:select", (e) => {
    const block = e.target;
    if (block && block.closest("gameplan-cards-component")) {
      block.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  });
}
